// The panel beside the page: styles, inserting, comments, history and the
// AI, each working through the same edits as everything else.
import { useEffect, useState } from "preact/hooks";
import { memo } from "preact/compat";
import type { Editor } from "./editor";
import { components, describeProp, type Prop } from "./components";
import { PROPERTIES, TOKENS } from "./style";
import { HostContext, MadeContext, PageView, defsOf, type Host } from "./render";
import { plain, type Expose } from "./library";
import { HIDDEN, type NodeJSON, type TreeID } from "./doc";
import { textOf, type Version } from "./history";
import { ask, PROVIDERS, selectionText, Suggestion, type Provider, type Settings } from "./ai";
import { Button, Input, NativeSelect, Tabs, TabsContent, TabsList, TabsTrigger, Textarea, icons, toast } from "../ui";

const Pencil = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21.17 6.81a1 1 0 0 0-3.99-3.99L3.84 16.17a2 2 0 0 0-.5.83l-1.32 4.35a.5.5 0 0 0 .62.62l4.35-1.32a2 2 0 0 0 .83-.5z" /></svg>
);

export type Tab = "style" | "insert" | "comments" | "history" | "ai";
const TABS: [Tab, string][] = [["style", "Style"], ["insert", "Insert"], ["comments", "Comments"], ["history", "History"], ["ai", "AI"]];

export function Panel({ editor, host, tab, setTab, picking, setPicking }: {
  editor: Editor; host: Host; tab: Tab; setTab: (t: Tab) => void; picking: boolean; setPicking: (v: boolean) => void;
}) {
  return (
    <aside class="side" aria-label="Panel">
      {/* setTab is a state setter, so the tabs' context stays the same
          object between keystrokes and the list doesn't redraw. */}
      <Tabs value={tab} onValueChange={setTab as (t: string) => void} class="side-tabs-root">
        <PanelTabs />
        {/* Only the open tab's panel is drawn, as before. */}
        <TabsContent value={tab} class="side-body" data-panel={tab}>
          {tab === "style" && <StylePanel editor={editor} picking={picking} setPicking={setPicking} />}
          {tab === "insert" && <InsertPanel editor={editor} host={host} />}
          {tab === "comments" && <CommentsPanel editor={editor} />}
          {tab === "history" && <HistoryPanel editor={editor} />}
          {tab === "ai" && <AIPanel editor={editor} />}
        </TabsContent>
      </Tabs>
    </aside>
  );
}

const PanelTabs = memo(() => (
  <TabsList class="side-tabs">
    {TABS.map(([t, label]) => (
      <TabsTrigger value={t} data-tab={t} key={t}>{label}</TabsTrigger>
    ))}
  </TabsList>
));

export function find(nodes: NodeJSON[], id: TreeID | undefined): NodeJSON | undefined {
  if (id === undefined) return undefined;
  for (const n of nodes) {
    if (n.id === id) return n;
    const f = find(n.children, id);
    if (f) return f;
  }
}

const Why = ({ why }: { why?: string }) => (why ? <p class="why" role="alert">{why}</p> : null);
const Reset = ({ label, onClick }: { label: string; onClick: () => void }) => (
  <Button variant="ghost" size="icon-sm" class="reset" aria-label={label} title="Back to the default" onClick={onClick}><icons.X /></Button>
);
const nameOf = (editor: Editor, n: NodeJSON) => editor.library[n.type]?.label ?? n.type;

// ---------- style ----------

// Where each of a node's properties comes from: set on it, a theme token,
// or its component's default.
// meets: style/inspect
export type Row = { prop: string; value: string; source: "set" | "token" | "default"; token?: string; here: boolean };
export function inspect(node: NodeJSON): Row[] {
  const c = components[node.type];
  const rows: Row[] = [];
  for (const prop of Object.keys(PROPERTIES)) {
    const set = node.style[prop];
    const v = set ?? c?.style?.[prop];
    if (v === undefined) continue;
    const token = v.match(/^var\((--[a-z][a-z0-9-]*)\)$/)?.[1];
    rows.push({ prop, value: v, source: token ? "token" : set !== undefined ? "set" : "default", token, here: set !== undefined });
  }
  return rows;
}

function StylePanel({ editor, picking, setPicking }: { editor: Editor; picking: boolean; setPicking: (v: boolean) => void }) {
  const node = find(editor.page.roots(), editor.selected);
  const [why, setWhy] = useState<string>();
  const [adding, setAdding] = useState("color");
  const [addValue, setAddValue] = useState("");
  useEffect(() => setWhy(undefined), [editor.selected]);
  const el = node ? (document.querySelector(`.page [data-node="${node.id}"]`) as HTMLElement | null) : null;
  const computed = el ? getComputedStyle(el) : undefined;
  const set = (prop: string, value: string | null) => {
    if (!node) return;
    setWhy(editor.page.setStyle(node.id, prop, value) ? undefined : `“${value}” isn't an allowed value for ${prop}.`);
  };
  const tokens = editor.page.tokens.toJSON() as Record<string, string>;
  return (
    <div class="style-panel">
      <Button variant={picking ? "default" : "outline"} class="pencil" aria-pressed={picking} onClick={() => setPicking(!picking)}>
        <Pencil /> {picking ? "Picking: click any element" : "Pick an element"}
      </Button>
      {node ? (
        <section class="inspect" data-inspect={node.id}>
          <h3>{nameOf(editor, node)} <code>{node.id}</code></h3>
          <table class="rows">
            <tbody>
              {inspect(node).map((r) => (
                <tr key={r.prop} data-prop={r.prop} data-source={r.source} data-here={r.here ? "1" : undefined}>
                  <th scope="row">{r.prop}</th>
                  <td>
                    <div class="value">
                      <Input
                        aria-label={r.prop}
                        value={r.value}
                        onChange={(e) => set(r.prop, (e.target as HTMLInputElement).value)}
                      />
                      {r.here && (
                        <Reset label={`Reset ${r.prop}`} onClick={() => set(r.prop, null)} />
                      )}
                    </div>
                    <div class="meta">
                      <span class={`src src-${r.source}`}>
                        {r.source === "token" ? `token ${r.token}` : r.source === "set" ? "set here" : "default"}
                        {r.source === "token" && r.here ? ", set here" : ""}
                      </span>
                      <span class="computed" title="As drawn">{computed?.getPropertyValue(r.prop)}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <form class="add" onSubmit={(e) => (e.preventDefault(), set(adding, addValue), setAddValue(""))}>
            <NativeSelect aria-label="Property" value={adding} onChange={(e) => setAdding((e.target as HTMLSelectElement).value)}>
              {Object.entries(PROPERTIES).map(([p, d]) => <option value={p} key={p}>{d.group}: {p}</option>)}
            </NativeSelect>
            <Input aria-label="Value" placeholder="value" value={addValue} onInput={(e) => setAddValue((e.target as HTMLInputElement).value)} />
            <Button size="sm" variant="default" type="submit">Set</Button>
          </form>
          <Why why={why} />
        </section>
      ) : (
        <p class="hint">Pick an element, or click a block's handle, to see its styles.</p>
      )}
      <section class="tokens">
        <h3>Theme</h3>
        <p class="hint">Change a token and everything that uses it changes.</p>
        <table class="rows">
          <tbody>
            {[...new Set([...Object.keys(TOKENS), ...Object.keys(tokens)])].map((k) => (
              <tr key={k} data-token={k}>
                <th scope="row">{k}</th>
                <td>
                  <Input
                    aria-label={k}
                    value={tokens[k] ?? TOKENS[k]}
                    onChange={(e) => {
                      const ok = editor.page.setToken(k, (e.target as HTMLInputElement).value);
                      setWhy(ok ? undefined : `“${(e.target as HTMLInputElement).value}” can't be a token's value.`);
                    }}
                  />
                </td>
                <td>
                  <span class="src">{tokens[k] !== undefined ? "this page" : "default"}</span>
                  {tokens[k] !== undefined && <Reset label={`Reset ${k}`} onClick={() => editor.page.setToken(k, null)} />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

// ---------- insert, settings, made components ----------

function InsertPanel({ editor, host }: { editor: Editor; host: Host }) {
  const lib = editor.library;
  const node = find(editor.page.roots(), editor.selected);
  const [editing, setEditing] = useState<string>();
  return (
    <div class="insert-panel">
      <section>
        <h3>Insert</h3>
        <p class="hint">After the selected block, or type <kbd>/</kbd> in an empty one.</p>
        <Library editor={editor} />
      </section>
      {node && !HIDDEN.has(node.type) && lib[node.type] && <PropsForm editor={editor} node={node} />}
      {node && !HIDDEN.has(node.type) && !lib[node.type] && <TagForm editor={editor} node={node} />}
      {editor.chosen.length > 0 && <Compose editor={editor} />}
      {editor.definitions.length > 0 && (
        <section class="made">
          <h3>Made on this page</h3>
          {editor.definitions.map((d) => {
            const name = String(d.props.name);
            return (
              <div class="def" key={d.id} data-definition={name}>
                <Button size="sm" variant="outline" aria-pressed={editing === name} onClick={() => setEditing(editing === name ? undefined : name)}>
                  {editing === name ? "Done" : "Edit"} {name}
                </Button>
                {editing === name && (
                  <div class="page def-edit">
                    <HostContext.Provider value={host}>
                      <MadeContext.Provider value={{ defs: defsOf(editor.definitions), within: [name] }}>
                        <PageView nodes={d.children} />
                      </MadeContext.Provider>
                    </HostContext.Provider>
                  </div>
                )}
              </div>
            );
          })}
        </section>
      )}
    </div>
  );
}

// The library as buttons in the panel, or as a menu's items under `/`.
export function Library({ editor, onDone, menu }: { editor: Editor; onDone?: () => void; menu?: boolean }) {
  return (
    <ul class="library">
      {Object.values(editor.library).map((e) => (
        <li key={e.name}>
          {menu ? (
            <button type="button" role="menuitem" class="ui-menu-item" data-insert={e.name} onClick={() => (editor.insert(e.name), onDone?.())}>
              {e.label}{e.made ? " (made here)" : ""}
            </button>
          ) : (
            <Button variant="outline" size="sm" data-insert={e.name} onClick={() => (editor.insert(e.name), onDone?.())}>
              {e.label}{e.made ? " (made here)" : ""}
            </Button>
          )}
        </li>
      ))}
    </ul>
  );
}

// A component's settings, as a form made from its schema; a value that
// breaks the schema is refused, with what it must be.
// meets: insert/props
function PropsForm({ editor, node }: { editor: Editor; node: NodeJSON }) {
  const entry = editor.library[node.type];
  const [why, setWhy] = useState<string>();
  useEffect(() => setWhy(undefined), [node.id]);
  const props = Object.entries(entry.props);
  const set = (k: string, v: unknown) => setWhy(editor.page.setProp(node.id, k, v));
  return (
    <section class="props" data-props={node.id}>
      <h3>{entry.label} settings</h3>
      {!props.length && <p class="hint">It has none.</p>}
      {props.map(([k, p]) => (
        <Field key={`${node.id}:${k}`} name={k} prop={p} value={node.props[k] ?? p.default} set={(v) => set(k, v)} />
      ))}
      <Why why={why} />
    </section>
  );
}

// A component this build doesn't have, such as a tag the site draws: its
// props are text, edited as written.
// meets: file/unknown-tags
function TagForm({ editor, node }: { editor: Editor; node: NodeJSON }) {
  const [why, setWhy] = useState<string>();
  useEffect(() => setWhy(undefined), [node.id]);
  const props = Object.entries(node.props).filter(([, v]) => typeof v === "string");
  return (
    <section class="props" data-props={node.id}>
      <h3>&lt;{node.type} /&gt; settings</h3>
      <p class="hint">This build doesn't draw it; its settings are kept as written.</p>
      {props.map(([k, v]) => (
        <label class="field" data-kind="string" key={`${node.id}:${k}`}>
          <span>{k}</span>
          <Input type="text" name={k} value={v as string} onChange={(e) => setWhy(editor.page.setProp(node.id, k, (e.target as HTMLInputElement).value))} />
        </label>
      ))}
      <Why why={why} />
    </section>
  );
}

function Field({ name, prop, value, set }: { name: string; prop: Prop; value: unknown; set: (v: unknown) => void }) {
  const label = <span>{name}</span>;
  switch (prop.kind) {
    case "string":
      return <label class="field" data-kind="string">{label}<Input type="text" name={name} value={String(value)} title={describeProp(prop)} onChange={(e) => set((e.target as HTMLInputElement).value)} /></label>;
    case "number":
      return <label class="field" data-kind="number">{label}<Input type="number" name={name} value={String(value)} title={describeProp(prop)} onChange={(e) => set((e.target as HTMLInputElement).valueAsNumber)} /></label>;
    case "enum":
      return (
        <label class="field" data-kind="enum">{label}
          <NativeSelect name={name} value={String(value)} onChange={(e) => set((e.target as HTMLSelectElement).value)}>
            {prop.options.map((o) => <option key={o} value={o}>{o}</option>)}
          </NativeSelect>
        </label>
      );
    case "boolean":
      return <label class="field check" data-kind="boolean"><input type="checkbox" class="ui-check" name={name} checked={value === true} onChange={(e) => set((e.target as HTMLInputElement).checked)} />{label}</label>;
  }
}

// Make a component from the chosen blocks, exposing the fields ticked.
// meets: compose/new
function Compose({ editor }: { editor: Editor }) {
  const [name, setName] = useState("");
  const [why, setWhy] = useState<string>();
  const [ticked, setTicked] = useState<Record<string, string>>({});
  const roots = editor.page.roots();
  const chosen = editor.chosen.map((id) => find(roots, id)).filter((n): n is NodeJSON => !!n);
  // Every field in the chosen blocks an instance might want to change.
  const fields: { key: string; node: TreeID; field: string; label: string; suggest: string }[] = [];
  const count: Record<string, number> = {};
  const walk = (n: NodeJSON) => {
    if (n.text) {
      const base = n.type === "heading" ? "title" : n.type === "paragraph" ? "body" : n.type;
      const k = count[base] ? `${base}${++count[base]}` : ((count[base] = 1), base);
      fields.push({ key: `${n.id}:text`, node: n.id, field: "text", label: `${n.type}'s words: “${plain(n.text).slice(0, 24)}”`, suggest: k });
    }
    for (const k of Object.keys(components[n.type]?.props ?? {}))
      fields.push({ key: `${n.id}:prop:${k}`, node: n.id, field: `prop:${k}`, label: `${n.type}'s ${k}`, suggest: k });
    n.children.forEach(walk);
  };
  chosen.forEach(walk);
  return (
    <section class="compose">
      <h3>Make a component</h3>
      <p class="hint">From the {chosen.length === 1 ? "selected block" : `${chosen.length} selected blocks`}; shift-click handles to choose more. Tick what each copy may change.</p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const expose: Expose = {};
          for (const f of fields) if (ticked[f.key] !== undefined) expose[ticked[f.key] || f.suggest] = { node: f.node, field: f.field };
          const r = editor.page.compose(chosen.map((n) => n.id), name.trim(), expose);
          if ("error" in r) return setWhy(r.error);
          setWhy(undefined);
          setName("");
          setTicked({});
          editor.select(r.id);
        }}
      >
        <label class="field">
          <span>Name</span>
          <Input name="component-name" placeholder="Card" value={name} onInput={(e) => setName((e.target as HTMLInputElement).value)} />
        </label>
        {fields.map((f) => (
          <label class="field check" key={f.key}>
            <input
              type="checkbox" class="ui-check"
              data-expose={f.key}
              checked={ticked[f.key] !== undefined}
              onChange={(e) => {
                const next = { ...ticked };
                if ((e.target as HTMLInputElement).checked) next[f.key] = f.suggest;
                else delete next[f.key];
                setTicked(next);
              }}
            />
            <span>{f.label}</span>
            {ticked[f.key] !== undefined && (
              <Input class="as" aria-label="Its name" value={ticked[f.key]} onInput={(e) => setTicked({ ...ticked, [f.key]: (e.target as HTMLInputElement).value })} />
            )}
          </label>
        ))}
        <Button size="sm" variant="default" type="submit">Make component</Button>
      </form>
      <Why why={why} />
    </section>
  );
}

// ---------- comments ----------

function CommentsPanel({ editor }: { editor: Editor }) {
  const [text, setText] = useState("");
  const [show, setShow] = useState<"open" | "resolved">("open");
  const threads = editor.comments.list();
  const r = editor.range;
  const onWords = r && r.to > r.from && r.node === editor.selected ? r : undefined;
  const target = onWords?.node ?? editor.selected;
  const node = find(editor.page.roots(), target);
  const quote = onWords ? editor.page.text(onWords.node)?.toString().slice(onWords.from, onWords.to) : undefined;
  const me = editor.presence.name;
  const shown = threads.filter((t) => t.resolved === (show === "resolved"));
  return (
    <div class="comments-panel">
      <form
        class="compose-comment"
        onSubmit={(e) => {
          e.preventDefault();
          if (!target) return;
          editor.comments.add(target, text, me, onWords);
          setText("");
        }}
      >
        <p class="hint">
          {node ? (quote ? <>On <q>{quote}</q></> : <>On the {nameOf(editor, node)}</>) : "Select some words, or a block, to comment on it."}
        </p>
        <Textarea name="comment" rows={3} placeholder="Add a comment" value={text} onInput={(e) => setText((e.target as HTMLTextAreaElement).value)} disabled={!node} />
        <Button size="sm" variant="default" type="submit" disabled={!node || !text.trim()}>Comment</Button>
      </form>
      <div class="filter" role="group" aria-label="Show">
        <Button size="sm" variant="outline" aria-pressed={show === "open"} onClick={() => setShow("open")}>Open ({threads.filter((t) => !t.resolved).length})</Button>
        <Button size="sm" variant="outline" aria-pressed={show === "resolved"} onClick={() => setShow("resolved")}>Resolved ({threads.filter((t) => t.resolved).length})</Button>
      </div>
      <ul class="threads">
        {shown.map((t) => (
          <Thread key={t.id} editor={editor} t={t} />
        ))}
      </ul>
    </div>
  );
}

function Thread({ editor, t }: { editor: Editor; t: ReturnType<Editor["comments"]["list"]>[number] }) {
  const [reply, setReply] = useState("");
  const now = t.from !== undefined ? editor.page.text(t.node)?.toString().slice(t.from, t.to) : undefined;
  return (
    <li class={`thread${t.resolved ? " resolved" : ""}`} data-thread={t.id} data-node={undefined} data-on={t.node}>
      <button type="button" class="on" onClick={() => editor.select(t.node)}>
        {t.gone ? "On a block that was deleted" : now !== undefined ? <>On <q>{now}</q></> : <>On the {editor.page.type(t.node)}</>}
      </button>
      {t.messages.map((m, i) => (
        <p class="message" key={i}><b>{m.by}</b> {m.text}</p>
      ))}
      <form onSubmit={(e) => (e.preventDefault(), editor.comments.reply(t.id, reply, editor.presence.name), setReply(""))}>
        <Input name="reply" placeholder="Reply" value={reply} onInput={(e) => setReply((e.target as HTMLInputElement).value)} />
        <Button size="sm" variant="default" type="submit" disabled={!reply.trim()}>Reply</Button>
        <Button size="sm" variant="outline" class="resolve" onClick={() => editor.comments.resolve(t.id, !t.resolved)}>{t.resolved ? "Reopen" : "Resolve"}</Button>
      </form>
    </li>
  );
}

// ---------- history ----------

function HistoryPanel({ editor }: { editor: Editor }) {
  const versions = editor.history.versions();
  const [open, setOpen] = useState<number>();
  return (
    <div class="history-panel">
      <Button size="sm" variant="outline" onClick={() => (editor.mark(), toast("Version marked"))}>Mark this version</Button>
      <p class="hint">A version is marked when a session of editing goes quiet. Returning to one is a new edit; nothing after it is lost.</p>
      <ol class="versions" reversed>
        {[...versions].reverse().map((v) => (
          <li key={v.n} data-version={v.n}>
            <div class="v-head">
              <span>Version {v.n + 1}</span>
              <time>{new Date(v.at).toLocaleString()}</time>
            </div>
            <div class="authors">{v.authors.join(", ") || "—"}</div>
            <Button size="sm" variant="outline" aria-pressed={open === v.n} onClick={() => setOpen(open === v.n ? undefined : v.n)}>{open === v.n ? "Close" : "View"}</Button>
            <Button size="sm" variant="ghost" class="restore" onClick={() => (editor.history.restore(v), editor.mark(), toast(`Back to version ${v.n + 1}`, { description: "Nothing after it is lost." }))}>Restore</Button>
            {open === v.n && <Preview editor={editor} v={v} />}
          </li>
        ))}
      </ol>
    </div>
  );
}

function Preview({ editor, v }: { editor: Editor; v: Version }) {
  const then = editor.history.at(v);
  const lines = diff(textOf(then).split("\n"), textOf(editor.page).split("\n"));
  return (
    <div class="preview">
      <div class="page version-page" data-version-page={v.n}>
        <MadeContext.Provider value={{ defs: defsOf(then.definitions()), within: [] }}>
          <PageView nodes={then.toJSON()} />
        </MadeContext.Provider>
      </div>
      <h4>Since then</h4>
      <pre class="diff">
        {lines.filter((l) => l[0] !== " ").map((l, i) => <div key={i} class={l[0] === "+" ? "add" : "del"}>{l}</div>)}
        {lines.every((l) => l[0] === " ") && "No changes."}
      </pre>
    </div>
  );
}

// Lines of a and b as an LCS diff: " " kept, "-" only in a, "+" only in b.
export function diff(a: string[], b: string[]): string[] {
  const m = a.length, n = b.length;
  const L = Array.from({ length: m + 1 }, () => new Uint16Array(n + 1));
  for (let i = m - 1; i >= 0; i--) for (let j = n - 1; j >= 0; j--) L[i][j] = a[i] === b[j] ? L[i + 1][j + 1] + 1 : Math.max(L[i + 1][j], L[i][j + 1]);
  const out: string[] = [];
  let i = 0, j = 0;
  while (i < m && j < n) {
    if (a[i] === b[j]) out.push(" " + a[i++]), j++;
    else if (L[i + 1][j] >= L[i][j + 1]) out.push("-" + a[i++]);
    else out.push("+" + b[j++]);
  }
  while (i < m) out.push("-" + a[i++]);
  while (j < n) out.push("+" + b[j++]);
  return out;
}

// ---------- AI ----------

const SETTINGS = "editor:ai";
function loadSettings(): Settings {
  try {
    const s = JSON.parse(localStorage.getItem(SETTINGS) ?? "null");
    if (s && (s.provider === "anthropic" || s.provider === "openai")) return { on: !!s.on, provider: s.provider, model: String(s.model ?? ""), key: String(s.key ?? "") };
  } catch {}
  return { on: false, provider: "anthropic", model: PROVIDERS.anthropic.model, key: "" };
}

function AIPanel({ editor }: { editor: Editor }) {
  const [s, setS] = useState<Settings>(loadSettings);
  const [instruction, setInstruction] = useState("");
  const [busy, setBusy] = useState(false);
  const [why, setWhy] = useState<string>();
  const suggestion = editor.suggestion;
  const setSuggestion = (s?: Suggestion) => ((editor.suggestion = s), editor.changed());
  const [chosen, setChosen] = useState<number[]>(() => suggestion?.changes.flatMap((c, i) => (c.result.ok ? [i] : [])) ?? []);
  const save = (next: Settings) => {
    setS(next);
    try {
      localStorage.setItem(SETTINGS, JSON.stringify(next));
    } catch {}
  };
  const ids = editor.chosen;
  const run = async () => {
    setWhy(undefined);
    setBusy(true);
    const agent = editor.agent;
    // The AI is on the page while it works, like anyone else.
    editor.presence.speakFor(agent.peer, { name: agent.name, hue: 285, node: ids[0] });
    try {
      const calls = await ask(s, editor.page, instruction, selectionText(editor.page, ids));
      const sug = new Suggestion(editor.page, calls);
      setSuggestion(sug);
      setChosen(sug.changes.flatMap((c, i) => (c.result.ok ? [i] : [])));
      if (!calls.length) setWhy("The AI suggested no changes.");
    } catch (e) {
      setWhy(String((e as Error)?.message ?? e));
    } finally {
      editor.presence.speakFor(agent.peer, undefined);
      setBusy(false);
    }
  };
  return (
    <div class="ai-panel">
      <section class="ai-settings">
        <label class="field check">
          <input type="checkbox" class="ui-check" name="ai-on" checked={s.on} onChange={(e) => save({ ...s, on: (e.target as HTMLInputElement).checked })} />
          <span>Use an AI</span>
        </label>
        <p class="hint">
          {s.on
            ? `When you ask, the selected blocks, and nothing else about the page, go to ${PROVIDERS[s.provider].label}, from this browser.`
            : "Off: nothing about this page leaves this browser."}
        </p>
        {s.on && (
          <>
            <label class="field">
              <span>Provider</span>
              <NativeSelect name="ai-provider" value={s.provider} onChange={(e) => {
                const provider = (e.target as HTMLSelectElement).value as Provider;
                save({ ...s, provider, model: PROVIDERS[provider].model });
              }}>
                {Object.entries(PROVIDERS).map(([k, p]) => <option key={k} value={k}>{p.label}</option>)}
              </NativeSelect>
            </label>
            <label class="field"><span>Model</span><Input name="ai-model" value={s.model} onChange={(e) => save({ ...s, model: (e.target as HTMLInputElement).value })} /></label>
            <label class="field"><span>Your key</span><Input name="ai-key" type="password" autocomplete="off" value={s.key} onChange={(e) => save({ ...s, key: (e.target as HTMLInputElement).value })} /></label>
          </>
        )}
      </section>
      {s.on && (
        <section class="ask">
          <p class="hint">{ids.length ? `About the ${ids.length === 1 ? "selected block" : `${ids.length} selected blocks`}.` : "Select a block first: its handle, or click in its words."}</p>
          <Textarea name="ai-ask" rows={3} placeholder="Make this friendlier" value={instruction} onInput={(e) => setInstruction((e.target as HTMLTextAreaElement).value)} />
          <Button size="sm" disabled={busy || !ids.length || !instruction.trim()} onClick={run}>{busy ? "Asking…" : "Ask"}</Button>
        </section>
      )}
      <Why why={why} />
      {suggestion && (
        <section class="suggestion">
          <h3>Suggested</h3>
          <ul class="changes">
            {suggestion.changes.map((c, i) => (
              <li key={i} data-change={i} class={c.result.ok ? "" : "refused"}>
                <label class="field check">
                  <input type="checkbox" class="ui-check" disabled={!c.result.ok} checked={chosen.includes(i)} onChange={(e) => setChosen((e.target as HTMLInputElement).checked ? [...chosen, i] : chosen.filter((x) => x !== i))} />
                  <span>{c.text}</span>
                </label>
                {!c.result.ok && <span class="why">Refused: {c.result.reason}</span>}
              </li>
            ))}
          </ul>
          <Button size="sm" variant="default" class="accept" disabled={!chosen.length} onClick={() => {
            editor.agent.accept(suggestion, chosen);
            setSuggestion(undefined);
            toast(`Accepted ${chosen.length === 1 ? "1 change" : `${chosen.length} changes`}`);
          }}>Accept {chosen.length === suggestion.changes.length ? "all" : `${chosen.length} of ${suggestion.changes.length}`}</Button>
          <Button size="sm" variant="outline" onClick={() => setSuggestion(undefined)}>Discard</Button>
        </section>
      )}
      {editor.aiCanUndo && <Button size="sm" variant="outline" class="ai-undo" onClick={() => (editor.agent.undo(), editor.changed())}>Undo the AI's last edit</Button>}
    </div>
  );
}
